import React, { useState } from "react";
import {
  Card,
  CardContent,
  Stack,
  Typography,
  Button,
  Alert,
} from "@mui/material";
import {
  PersonAdd as PersonAddIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { fetchAuthSession } from "aws-amplify/auth";
import { UserTable } from "../components/users/UserTable";
import { UserForm } from "../components/users/UserForm";
import { UserGroups } from "../components/users/UserGroups";
import type { User } from "@/types/auth";

/**
 * Página de gestión de usuarios
 * CRUD completo de usuarios con sincronización Cognito
 *
 * REGLAS APLICADAS:
 * - Solo <Stack> para layout (NO Grid)
 * - Todos los componentes con size="small"
 * - CRUD completo implementado
 * - Máximo 500 líneas
 */

interface UserFormData {
  email: string;
  nombre: string;
  apellido: string;
  telefono: string;
}

export function UsersPage(): React.ReactElement {
  const [refreshKey, setRefreshKey] = useState(0);
  const [userForm, setUserForm] = useState<{
    open: boolean;
    user?: User | null;
  }>({
    open: false,
    user: null,
  });
  const [userGroups, setUserGroups] = useState<{
    open: boolean;
    user?: User | null;
  }>({
    open: false,
    user: null,
  });
  const [actionError, setActionError] = useState<string | null>(null);

  // Funciones para manejar acciones de la tabla
  const handleEdit = (user: User) => {
    setUserForm({ open: true, user });
  };

  const handleDelete = async (user: User): Promise<void> => {
    try {
      // Obtener el token de autenticación
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        throw new Error("No se pudo obtener el token de autenticación");
      }

      const response = await fetch(`/api/users/${user.id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            `Error ${response.status}: ${response.statusText}`,
        );
      }

      // Actualizar la tabla
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Error eliminando usuario:", error);
      throw error; // Re-throw para que UserTable pueda manejar el error
    }
  };

  const handleAssignGroups = (user: User) => {
    setUserGroups({ open: true, user });
  };

  const handleToggleStatus = async (user: User): Promise<void> => {
    try {
      // Obtener el token de autenticación
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        throw new Error("No se pudo obtener el token de autenticación");
      }

      const newStatus = !user.activo;
      const endpoint = newStatus ? "activate" : "deactivate";
      const response = await fetch(`/api/users/${user.id}/${endpoint}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            `Error ${response.status}: ${response.statusText}`,
        );
      }

      // Actualizar la tabla
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Error cambiando estado del usuario:", error);
      throw error;
    }
  };

  // Función para crear/editar usuario
  const handleSaveUser = async (userData: UserFormData): Promise<void> => {
    try {
      // Obtener el token de autenticación
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        throw new Error("No se pudo obtener el token de autenticación");
      }

      const isEditing = Boolean(userForm.user);
      const url = isEditing ? `/api/users/${userForm.user!.id}` : "/api/users";
      const method = isEditing ? "PATCH" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            `Error ${response.status}: ${response.statusText}`,
        );
      }

      // Actualizar la tabla
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Error guardando usuario:", error);
      throw error;
    }
  };

  // Función para asignar grupos
  const handleSaveUserGroups = async (
    userId: string,
    groupIds: string[],
  ): Promise<void> => {
    try {
      // Obtener el token de autenticación
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        throw new Error("No se pudo obtener el token de autenticación");
      }

      const response = await fetch(`/api/users/${userId}/groups`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ groupIds }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            `Error ${response.status}: ${response.statusText}`,
        );
      }

      // Actualizar la tabla
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Error asignando grupos:", error);
      throw error;
    }
  };

  const handleCreateNew = () => {
    setUserForm({ open: true, user: null });
  };

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
    setActionError(null);
  };

  return (
    <Stack spacing={4}>
      {/* Encabezado */}
      <Stack spacing={1}>
        <Typography variant="h4" component="h1">
          Gestión de Usuarios
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Administración de usuarios sincronizados con AWS Cognito
        </Typography>
      </Stack>

      {/* Error de acciones */}
      {actionError && (
        <Alert severity="error" onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {/* Acciones principales */}
      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={handleCreateNew}
        >
          Crear Usuario
        </Button>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
        >
          Actualizar
        </Button>
      </Stack>

      {/* Tabla de usuarios */}
      <Card>
        <CardContent>
          <UserTable
            key={refreshKey}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onAssignGroups={handleAssignGroups}
            onToggleStatus={handleToggleStatus}
          />
        </CardContent>
      </Card>

      {/* Modal de formulario de usuario */}
      <UserForm
        open={userForm.open}
        onClose={() => setUserForm({ open: false, user: null })}
        onSave={handleSaveUser}
        user={userForm.user || null}
      />

      {/* Modal de asignación de grupos */}
      <UserGroups
        open={userGroups.open}
        onClose={() => setUserGroups({ open: false, user: null })}
        onSave={handleSaveUserGroups}
        user={userGroups.user || null}
      />
    </Stack>
  );
}
